import React, { useContext, useEffect, useMemo } from 'react';

import { formatCurrency, formatPercent } from 'configs/columns';
import { useFeatureFlagVersion } from 'context/FeatureFlagContext';
import { CREATE_DEAL_MUTATION } from 'features/loanPool/PoolHeader';
import { Link, useNavigate } from 'react-router-dom';
import { getParticipationLabelFromRate } from 'features/pages/marketplace/ActiveTrades/shared/bidUtils';
import { Button, ProgressBar } from 'ui-kit';

import { useMutation, useQuery } from '@apollo/client';

import {
  GetCompanyRegulatoryMetrics,
  GetCompanyRegulatoryMetricsVariables,
} from 'query/__generated__/GetCompanyRegulatoryMetrics';
import { GET_COMPANY_REGULATORY_METRICS } from 'query/companyRegulatoryMetrics';

import { RequestAccessPublicListing } from './__generated__/RequestAccessPublicListing';
import { DataContext, DataContextActionType } from '../../context/DataContext';
import { GetUserInfo_user } from '../../query/__generated__/GetUserInfo';

type RequestAccessProps = {
  listingId: string;
  listingDetails: RequestAccessPublicListing;
  dealId?: string | null;
  user?: GetUserInfo_user | undefined;
  isMyListing?: boolean;
};

const RequestAccess: React.FC<RequestAccessProps> = ({
  listingDetails,
  listingId,
  dealId,
  user,
  isMyListing = false,
}) => {
  const navigate = useNavigate();
  const [createDeal, { loading: creatingDeal }] = useMutation(CREATE_DEAL_MUTATION);
  const bidsTableVersion = useFeatureFlagVersion('bidsTable');

  const { dispatch } = useContext(DataContext);

  useEffect(() => {
    dispatch({
      type: DataContextActionType.SET_LOADING,
      payload: creatingDeal,
    });
  }, [creatingDeal]);

  const { loading, data } = useQuery<
    GetCompanyRegulatoryMetrics,
    GetCompanyRegulatoryMetricsVariables
  >(GET_COMPANY_REGULATORY_METRICS, {
    variables: { id: listingId },
  });

  const company = data?.listing?.company;
  const listingCompanyId: string | null =
    'id' in listingDetails?.company ? listingDetails?.company?.id : null;

  const userCompanyId: string = user?.company?.id || '';

  const navigateToDeal = (id: string) => navigate(`/marketplace/deal/${encodeURIComponent(id)}`);

  const getActiveTradesUrl = () => {
    // If bidsTable feature flag is v1, use the tabbed interface with /all
    // Otherwise, use the base active_trades URL without tabs
    return bidsTableVersion === 'v1'
      ? '/marketplace/active_trades/all'
      : '/marketplace/active_trades';
  };

  const handleAccept = () => {
    createDeal({ variables: { listingId } }).then((response) => {
      const id = response.data?.createDeal.id;

      if (id) {
        navigateToDeal(id);
      }
    });
  };

  const scaledServicingRate = listingDetails.servicing_fee_rate_bps ?? 0;

  const stats = useMemo(() => {
    const rows: { label: string; value: string }[] = [
      {
        label: 'UPB',
        value: formatCurrency(listingDetails.t_current_balance_cents),
      },
      { label: 'GWAC', value: formatPercent(listingDetails.wa_coupon_bps ?? 0, 3) },
      {
        label: 'NWAC',
        value: formatPercent((listingDetails.wa_coupon_bps ?? 0) - scaledServicingRate, 3),
      },
      {
        label: 'WAM',
        value: listingDetails.wa_remaining_loan_terms_months
          ? `${listingDetails.wa_remaining_loan_terms_months} Mo`
          : 'NO DATA',
      },
      {
        label: 'WALA',
        value: listingDetails.wa_loan_age_months
          ? `${listingDetails.wa_loan_age_months} Mo`
          : 'NO DATA',
      },
    ];
    rows.push({
      label: 'Ownership',
      value: getParticipationLabelFromRate(listingDetails.participation_rate),
    });
    return rows;
  }, [listingDetails, scaledServicingRate]);

  const metrics = useMemo(() => {
    const regulatoryMetrics = company?.regulatory_metrics;

    return {
      'Loans/Shares': regulatoryMetrics?.loans_shares_rank ?? 0,
      Delinquency: regulatoryMetrics?.delinquency_rank ?? 0,
      'Charge Offs': regulatoryMetrics?.charge_off_ratio_rank ?? 0,
      'Net Worth Ratio': regulatoryMetrics?.net_worth_ratio_rank ?? 0,
      'Expense Ratio': regulatoryMetrics?.expense_ratio_rank ?? 0,
      ROA: regulatoryMetrics?.roa_rank ?? 0,
      ROE: regulatoryMetrics?.roe_rank ?? 0,
      'Allowance/Total Loans': regulatoryMetrics?.allowance_total_loans_rank ?? 0,
      'Efficiency Ratio': regulatoryMetrics?.efficiency_ratio_rank ?? 0,
      'IEA/Total Assets': regulatoryMetrics?.iea_total_assets_rank ?? 0,
    };
  }, [company]);

  const renderOfferDetails = () => {
    return (
      <div className="mt-3 w-full max-w-[290px]">
        <h3 className="mb-3 text-sm uppercase text-slate-200">Offer Details</h3>
        {stats.map(({ label, value }) => (
          <div key={label} className="flex justify-between border-t border-slate-600 px-3 py-2">
            <span className="text-sm text-slate-200">{label}</span>
            <span className="text-sm text-slate-200">{value}</span>
          </div>
        ))}
      </div>
    );
  };

  const averageIndex = company
    ? Object.values(metrics).reduce((acc: number, rank) => acc + (rank ?? 0), 0) /
      Object.values(metrics).length
    : 0;

  const handleRequestBtnClick = () => {
    if (dealId) {
      navigateToDeal(dealId);
    } else {
      handleAccept();
    }
  };

  const regulatoryAgencyLabel =
    company?.regulatory_metrics?.regulatory_agency &&
    company.regulatory_metrics.regulatory_agency !== 'NONE'
      ? company.regulatory_metrics.regulatory_agency
      : '—';

  return (
    <div className="relative">
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col justify-end pt-4">
          {loading ? (
            <SkeletonLoader />
          ) : (
            <>
              <div className="mb-2 flex justify-between border-b border-t border-slate-500 pb-[9px] pt-[7px]">
                <p className="text-sm uppercase text-slate-200">Regulatory agency</p>
                <p className="text-sm text-slate-200">{regulatoryAgencyLabel}</p>
              </div>
              <div className="mb-2 flex justify-between border-b border-t border-slate-500 pb-[9px] pt-[7px]">
                <div className="flex">
                  <p className="text-sm uppercase text-slate-200">Regulatory Index</p>
                </div>
                <p className="text-sm text-slate-200">{averageIndex}th Index</p>
              </div>

              {Object.entries(metrics).map(([label, rank]) => (
                <ProgressBar key={label} label={label} rank={rank} />
              ))}
            </>
          )}
        </div>
        <div className="flex min-h-0 flex-col">
          <div>
            <p className="mb-3 text-sm uppercase text-slate-200">
              {isMyListing ? 'Your Listing' : 'Pool Publish Settings'}
            </p>
            <p className="mb-2 text-sm text-slate-200">
              {isMyListing
                ? "This is your company's listing. You can manage active trades and view deal activity for this pool of loans."
                : (() => {
                    const isParticipation =
                      listingDetails.participation_rate != null &&
                      listingDetails.participation_rate < 100;
                    return (
                      <>
                        {isParticipation && (
                          <>
                            This is a <strong>participation</strong> offering (not a whole loan
                            sale).{' '}
                          </>
                        )}
                        Once the seller approves your request for data, you will be able to access
                        the loan tape, generate strats and run cashflows on this pool of loans, and
                        place a bid.
                      </>
                    );
                  })()}
            </p>
            <p className="text-sm text-slate-200">
              {isMyListing ? (
                'Manage your deals and view buyer activity in the Active Trades section.'
              ) : (
                <>
                  Track your deals in the{' '}
                  <Link
                    to={getActiveTradesUrl()}
                    className="uppercase text-purple-500 underline underline-offset-2"
                  >
                    Active Trades
                  </Link>{' '}
                  section.
                </>
              )}
            </p>
          </div>
          <div className="mt-3 w-full max-w-[290px]">{renderOfferDetails()}</div>
          <div className="mt-auto flex justify-end gap-2 pt-4">
            {isMyListing ? (
              <Button
                type="primary_green"
                size="small"
                label="View Trades"
                onClick={() => navigate(getActiveTradesUrl())}
              >
                View Active Trades
              </Button>
            ) : (
              userCompanyId !== listingCompanyId && (
                <Button
                  type="primary_green"
                  size="small"
                  label="Accept"
                  onClick={() => handleRequestBtnClick()}
                >
                  {dealId ? 'Go to Deal Room' : 'Request Access'}
                </Button>
              )
            )}
            <Button
              label="View"
              size="small"
              type="secondary_green"
              onClick={() => navigate(`/marketplace/listing/${listingId}`)}
            >
              View Stratifications
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SkeletonLoader: React.FC = () => (
  <div className="animate-pulse">
    <div className="mb-2 flex justify-between border-b border-t border-slate-500 pb-[9px] pt-[7px]">
      <div className="h-6 w-24 rounded bg-slate-700"></div>
      <div className="h-6 w-16 rounded bg-slate-700"></div>
    </div>

    {Array.from({ length: 8 }).map((_, index) => (
      <div key={index} className="mb-4 h-6 rounded bg-slate-700"></div>
    ))}
  </div>
);

export default RequestAccess;
